import React, { FC, useState, useRef, useEffect } from 'react';
import { ProgressBarMulti, ProgressBarMultiSize, Popover, Box, Text, Spinner, SpinnerSize } from '@bechtel/gds';
import {
  DashboardProgressRowStyled,
  ProgressContainerStyled,
  CalulationParametersStyled,
  TargetCalculationStyled,
} from './styled';
import { TARGET_HOURS_CALCULATION } from '@bechtel/client-gql';
import {
  TargetHoursCalculation,
  TargetHoursCalculationInput,
  PersonaType,
  CrewMember,
  WorkPackageStats,
} from '@bechtel/types';
import { get } from 'lodash';
import { ErrorBoundary, ErrorRetry, bankersRound, useConfig } from '@bechtel/client-common';
import { NetworkStatus, useQuery } from '@apollo/client';
import { t, Trans } from '@lingui/macro';

interface TargetHourPopperContentProps {
  crewMember: CrewMember | null | undefined;
}

export const TargetHourPopperContent: FC<TargetHourPopperContentProps> = ({ crewMember }) => {
  const { showInLinearFeet } = useConfig();
  const { loading, error, data, refetch, networkStatus } = useQuery<
    { targetHoursCalculation: TargetHoursCalculation },
    { input: TargetHoursCalculationInput }
  >(TARGET_HOURS_CALCULATION, {
    variables: {
      input: {
        crewMemberId: crewMember ? crewMember.id : '',
        persona: crewMember && crewMember.persona ? crewMember.persona : PersonaType.NONE,
      },
    },
    notifyOnNetworkStatusChange: true,
  });
  const targetHoursCalculation: TargetHoursCalculation | null = get(data, 'targetHoursCalculation', null);

  return (
    <Box style={{ width: '252px', userSelect: 'none' }} flexDirection={{ sm: 'column' }}>
      <Box p={{ sm: '400' }}>
        <Text size="300" family="graphikCompact" color="grey.1000" weight="600">
          <Trans>Your Target Hour Calculation</Trans>
        </Text>
      </Box>
      <ErrorBoundary>
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} py={{ sm: '400' }}>
            <Spinner size={SpinnerSize.MEDIUM} />
          </Box>
        )}

        {!loading && error && (
          <Box>
            <ErrorRetry refetch={refetch}>
              <Trans>Error loading target hours. Please retry.</Trans>
            </ErrorRetry>
          </Box>
        )}
        {!loading && !error && targetHoursCalculation && (
          <TargetCalculationStyled>
            <Box
              pr={{ sm: '400' }}
              flexDirection={{ sm: 'column' }}
              css={`
                ${({ theme }) => `border-right: 1px solid ${theme.colors.grey[200]};`}
              `}
            >
              <Box alignItems={{ sm: 'center' }}>
                <Trans>Headcount</Trans>
                <CalulationParametersStyled>{targetHoursCalculation.crewHeadCount}</CalulationParametersStyled>x
                <CalulationParametersStyled>{targetHoursCalculation.weeklyHours}</CalulationParametersStyled>
                <Trans>Hrs/Wk</Trans>
              </Box>
              <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                + <Trans>Indirect Tax</Trans>
                <CalulationParametersStyled>{`${bankersRound(
                  targetHoursCalculation.indirectTax * 100,
                  0
                )}%`}</CalulationParametersStyled>
              </Box>
              <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                <Trans>Project Multiplier</Trans>
                <CalulationParametersStyled>{`${bankersRound(
                  targetHoursCalculation.projectAdjustmentFactor * 100,
                  0
                )}%`}</CalulationParametersStyled>
              </Box>
              <Box pt={{ sm: '400' }}>
                <Box py={{ sm: '100' }}>
                  <Text weight="600">
                    <Trans>Hours Target</Trans>
                  </Text>
                </Box>
              </Box>
              {showInLinearFeet && (
                <Box pt={{ sm: '400' }}>
                  <Box py={{ sm: '100' }}>
                    <Text weight="600">
                      <Trans>LF Target</Trans>&nbsp;
                    </Text>
                    <Text color="grey.600">
                      <em>
                        (<Trans>approx unit rate </Trans>
                        {targetHoursCalculation.compositeUnitRate.unitRate} <Trans>hr/ft</Trans>)
                      </em>
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
            <Box pl={{ sm: '400' }} flexDirection={{ sm: 'column' }}>
              <Box alignItems={{ sm: 'center' }}>
                <Box py={{ sm: '100' }}>
                  <Text>
                    {targetHoursCalculation.crewHours} <Trans>hrs</Trans>
                  </Text>
                </Box>
              </Box>
              <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                <Box py={{ sm: '100' }}>
                  <Text>
                    {targetHoursCalculation.indirectHours} <Trans>hrs</Trans>
                  </Text>
                </Box>
              </Box>
              <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                <Box py={{ sm: '100' }}>
                  <Text>
                    +{targetHoursCalculation.projectAdjustmentHours} <Trans>hrs</Trans>
                  </Text>
                </Box>
              </Box>
              <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                <Box py={{ sm: '200' }}>
                  <Text weight="600">
                    {targetHoursCalculation.crewHours +
                      targetHoursCalculation.indirectHours +
                      targetHoursCalculation.projectAdjustmentHours}
                    &nbsp;<Trans>hrs</Trans>
                  </Text>
                </Box>
              </Box>
              {showInLinearFeet && (
                <Box pt={{ sm: '400' }} alignItems={{ sm: 'center' }}>
                  <Box py={{ sm: '100' }}>
                    <Text weight="600">
                      {`${
                        targetHoursCalculation.compositeUnitRate &&
                        targetHoursCalculation.compositeUnitRate.unitRate != 0
                          ? bankersRound(
                              (targetHoursCalculation.crewHours +
                                targetHoursCalculation.indirectHours +
                                targetHoursCalculation.projectAdjustmentHours) /
                                targetHoursCalculation.compositeUnitRate.unitRate,
                              0
                            )
                          : 0
                      }`}
                      &nbsp;<Trans>LF</Trans>
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
          </TargetCalculationStyled>
        )}
      </ErrorBoundary>
    </Box>
  );
};

export interface DashboardProgressProps {
  showTargetIndicator: boolean;
  progress: WorkPackageStats['progress'];
  plannedMilestones: WorkPackageStats['plannedMilestones'];
  crewMember: CrewMember | null | undefined;
}

export const DashboardProgress: FC<DashboardProgressProps> = ({
  showTargetIndicator,
  progress,
  plannedMilestones,
  crewMember,
}) => {
  const [show, setShow] = useState<boolean>(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const targetHours = progress.targetHours ? bankersRound(progress.targetHours, 0) : 0;

  //For field engineer, totalHours: 120% of planned/approved hours whichever is greater
  // and for other personas, it is 120% of target hours
  const totalHours = bankersRound(
    (crewMember?.persona === PersonaType.FIELD_ENGINEER
      ? progress.committedHours > progress.approvedHours
        ? progress.committedHours
        : progress.approvedHours
      : targetHours) * 1.2,
    0
  );

  const refs = {
    iconRef,
  };
  useEffect(() => {
    if (!showTargetIndicator || !refs || !refs.iconRef) return;
    if (!showTargetIndicator) {
      setShow(false); //super-user mode this can stay open when persona is switched so close if showTargetIndicator is false
      return;
    }
    const open = () => {
      setShow(!show);
    };
    refs.iconRef.current?.addEventListener('click', open);
    return () => {
      refs.iconRef.current?.removeEventListener('click', open);
    };
  }, [show, showTargetIndicator, refs]);
  return (
    <>
      <DashboardProgressRowStyled>
        <ProgressContainerStyled>
          <ProgressBarMulti
            data={[
              { label: t`Planned`, value: plannedMilestones ? plannedMilestones.hours : 0, fillColor: 'blue.400' },
              {
                label: t`Claimed`,
                value: progress.claimedHours,
                fillColor: 'orange.400',
              },
              {
                label: t`Approved`,
                value: progress.approvedHours,
                fillColor: 'green.400',
              },
            ]}
            bgColor={'white'}
            borderColor={'white'}
            totalValue={totalHours}
            units={t`Hrs`}
            showTargetIndicator={showTargetIndicator}
            showPercentage={false}
            targetValue={targetHours}
            labelColor={'white'}
            size={ProgressBarMultiSize.MEDIUM}
            ref={refs}
          />
        </ProgressContainerStyled>
      </DashboardProgressRowStyled>
      {refs.iconRef && show && (
        <Popover triggerComponentRef={refs.iconRef} placement="bottom" showPopper={show} customStyle={{ zIndex: 1 }}>
          <TargetHourPopperContent crewMember={crewMember} />
        </Popover>
      )}
    </>
  );
};
